import { APP_INITIALIZER, enableProdMode, importProvidersFrom} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors, HTTP_INTERCEPTORS, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';
import { routes } from './app/app-routing.module';

// Services
import { AuthGuard } from './app/_guards/auth.guard';
import {
  AuthenticationService,
  DocumentService,
  ProcessamentoService,
  LocationService,
  DataTableService,
  MenuService,
  EmpresaService,
  UserService,
  AlertService,
  AreaService,
  CategoriaService,
  ChartService,
  TreeService
} from './app/_services';
import { RealtimeService } from './app/_services/realtime.service';
import { BreadcrumbService } from './app/_services/breadcrumb.service';
import { SolrService } from './app/solr/solr.service';
import { RedisService } from './app/_services/redis_service';
import { SqsService } from './app/_services/sqs.service';
import { DicionarioOrtograficoService } from './app/_services/dicionarioOrtograficoService';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { SignatureService } from './app/_services/signature.service';
import { TaskService } from './app/_services/taskService';
import { RoleService } from './app/_services/role.service';
import { SwalService } from './app/_services/swal.service';
import { CalendarService } from './app/_services/calendar.service';
import { ActivityService } from './app/_services/activity.service';
import { ElementService } from './app/_services/element.service';
import { ControlSidebarService } from './app/_services/control-sidebar.service';
import { UrlService } from './app/_services/url.service';
import { IndexedDBService } from './app/_services/indexdDb.service';
import { FooterService } from './app/_services/footer.service';
import { LogoService } from './app/_services/logo.service';
import { MessagesService } from './app/_services/messages.service';
import { NotificationsService } from './app/_services/notifications.service';
import { LoaderService } from './app/_shared/loader/loader.service';
import { DocumentTypeService } from './app/_services/documentType.service';
import { VideoService } from './app/_services/video.service';
import { LoggerService } from './app/_services/logger.service';
import { PdfService } from './app/_services/pdf.service';
import { RestService } from './app/_services/rest.service';
import { NestedMetadataService } from './app/_services/nestedMetadata.service';

// Interceptors
import { JwtInterceptor } from './app/_interceptors/jwt.interceptor';
import { ErrorInterceptor } from './app/_interceptors/error.interceptor';
import { LoaderInterceptorService } from './app/_services/loader.interceptor.service';

// Modules for import
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpClient } from '@angular/common/http';
import { ToastrModule } from 'ngx-toastr';
import { NgxSpinnerModule } from 'ngx-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { UploadService } from './app/_shared/upload';
import { DownLoadService } from './app/_shared/download';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { redirectKtidentityRecoveryHash } from './app/_shared/ktidentity-hash-redirect/ktidentity-hash-redirect';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function initializeApp(dataService: AuthenticationService): () => Promise<any> {
  return (): Promise<any> => {
    console.log('APP_INITIALIZER started');
    return new Promise((resolve) => {
      console.log('Calling dataService.inicialize()');
      dataService.inicialize().then(data => {
        console.log('inicialize() completed:', data);
        const checkScripts = () => {
          if (typeof (window as any).$ !== 'undefined' && (window as any).$.fn) {
            console.log('jQuery found, resolving...');
            resolve(true);
          } else {
            console.log('Waiting for jQuery...');
            setTimeout(checkScripts, 50);
          }
        };

        checkScripts();
      }).catch(err => {
        console.error('Error in inicialize():', err);
        resolve(true); // Resolve anyway to not block the app
      });
    })
  };
}

if (environment.production) {
  enableProdMode();
}

// link de redefinição de senha do KTIdentity (#/recuperar-senha/...) vira rota por caminho antes do roteador iniciar
redirectKtidentityRecoveryHash(window.location, window.history);

console.log('Starting bootstrap...');

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    importProvidersFrom(
      CommonModule,
      FormsModule,
      ReactiveFormsModule,
      ToastrModule.forRoot({
        timeOut: 3000,
        positionClass: 'toast-bottom-right',
        preventDuplicates: true,
      }),
      MatProgressBarModule,
      MatDialogModule,
      MatFormFieldModule,
      MatAutocompleteModule,
      MatInputModule,
      MatSelectModule,
      MatCheckboxModule,
      MatButtonModule,
      MatTooltipModule,
      MatDatepickerModule,
      MatNativeDateModule,
      NgxDatatableModule,
      DragDropModule,
      NgxSpinnerModule,
      NgSelectModule,
      PdfViewerModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        }
      })
    ),
    // Services
    AuthGuard,
    AuthenticationService,
    UserService,
    AlertService,
    AreaService,
    CategoriaService,
    ChartService,
    MenuService,
    BreadcrumbService,
    SolrService,
    DocumentService,
    DocumentTypeService,
    SqsService,
    RealtimeService,
    ProcessamentoService,
    RedisService,
    BsModalService,
    BsModalRef,
    SignatureService,
    LocationService,
    TaskService,
    EmpresaService,
    RoleService,
    SwalService,
    CalendarService,
    ActivityService,
    DicionarioOrtograficoService,
    DataTableService,
    ElementService,
    ControlSidebarService,
    UrlService,
    TreeService,
    IndexedDBService,
    FooterService,
    LogoService,
    MessagesService,
    NotificationsService,
    LoaderService,
    VideoService,
    LoggerService,
    PdfService,
    RestService,
    NestedMetadataService,
    UploadService,
    DownLoadService,
    // Temporarily disabled to test bootstrap
    // {
    //   provide: APP_INITIALIZER,
    //   useValue: initializeApp,
    //   deps: [AuthenticationService],
    //   multi: true
    // },
    // Interceptors are now provided via withInterceptors
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptorService, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
  ]
}).then(() => {
  console.log('Bootstrap completed successfully');
}).catch(err => {
  console.error('Bootstrap error:', err);
});